<template>
  <v-container fluid class="custom-container">

    <!-- Status Cards -->
    <folder-status-cards 
      v-if="folders && folders.length > 0"
      :folders="folders" 
      :counts="inboxCounts" 
      context="inbox"
    />

    <!-- Folders Header -->

    <v-layout
      v-if="!isEmpty(groupedFolders)"
      class="mb-2 list-folders-actions align-center"
      row
      wrap
    >
      <v-flex xs12 sm6 md3>
        <div class="d-flex align-center">
          <strong v-if="!isEmpty(activeFolderFilterType)" class="mr-2">Filtrado por:</strong>
          <v-chip
            v-if="!isEmpty(activeFolderFilterType)"
            close
            @input="resetFolders"
            color="white"
          >{{ activeFolderFilterType.name }}
          </v-chip>
        </div>
      </v-flex>
      <v-flex xs12 sm6 md3>
        <div class="d-flex align-center">
          <span class="mr-2">Exibindo {{ pageStart }} - {{ pageStop }} de {{ foldersCount }}</span>
          <v-btn icon small @click="pageNumber--" :disabled="pageNumber <= 1">
            <v-icon>chevron_left</v-icon>
          </v-btn>
          <v-btn icon small @click="pageNumber++" :disabled="pageNumber >= pages">
            <v-icon>chevron_right</v-icon>
          </v-btn>
        </div>
      </v-flex>
      <v-flex xs12 sm6 md3>
        <v-select
          v-model="orderFilter"
          :items="orderFilters"
          item-text="name"
          return-object
          hide-details
          solo
          flat
          dense
        />
      </v-flex>
      <v-flex xs12 sm6 md3 class="justify-center" style="display: flex">
        <v-btn-toggle v-model="viewMode" mandatory dense>
          <v-btn flat small :value="'list'">
            <v-icon>mdi-format-list-bulleted</v-icon>
          </v-btn>
          <!--  <v-btn flat small :value="'grid'">
            <v-icon>mdi-grid</v-icon>
          </v-btn>-->
        </v-btn-toggle>
      </v-flex>
    </v-layout>

    <!-- Vista em Lista -->
    <template v-if="viewMode === 'list'">
      <section class="folders">
        <v-layout
          class="folder-list mt-4"
          v-for="(group,index) in groupedFolders"
          :key="index"
          column
        >
          <span
            class="subheading grey--text my-2 text-capitalize"
          > {{ index }}</span>

          <div class="elevation-1 group" v-if="group.length > 0">
              <transition-group
                name="fade-cascade"
                tag="div"
                class="cascade-group"
                appear
              >
                <folder-card
                  v-for="(folder, folderIndex) in group"
                  :key="folder.id"
                  :folder="folder"
                  :draggingItemId="draggingItemId"
                  :style="{ '--cascade-delay': `${folderIndex * 0.05}s` }"
                  @update:draggingItemId="id => draggingItemId = id"
                  @drag-start="startDrag"
                  @open-folder="openFolder"
                />
              </transition-group>
          </div>
        </v-layout>
      </section>
    </template>

    <!-- Vista em Grid -->
    <template v-else>
      <section class="folders">
        <v-layout
          class="folder-list mt-4"
          v-for="(group,index) in groupedFolders"
          :key="index"
          column
        >
          <span
            class="subheading grey--text my-2 text-capitalize"
          > {{ index }}</span>

          <folder-grid
            :folders="group"
            v-if="group.length > 0"
          />
        </v-layout>
      </section>
    </template>

    <!-- Empty Content -->
    <v-container fill-height v-if="isEmpty(groupedFolders) && !loading.show">
      <v-layout column align-center justify-center>
        <v-icon class="mb-2" size="48" color="grey darken-2">mdi-inbox</v-icon>
        <span class="title font-weight-regular grey--text text--darken-2">
          Não há itens para exibir
        </span>
      </v-layout>
    </v-container>


    <v-dialog
      v-model="showDialogNotice"
      persistent max-width="450">
      <v-card>
        <v-card-title class="headline green darken-2 white--text" primary-title>{{ currentNotice.title }}</v-card-title>
        <v-card-text class="newLine">{{ currentNotice.body }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" flat @click="close">Confirmar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>


<script>
import {isEmpty, truncate} from "lodash";
import acronym from "@/utils/acronym";
import shortName from "@/utils/short-name";
import format from "date-fns/format";
import * as ptLocale from "date-fns/locale/pt";
import bus from "@/utils/event-bus";
import CurrentOwner from "@/components/inbox/CurrentOwner";
import TermComponent from "@/components/inbox/TermComponent";
import NoticeService from "@/services/system/NoticeService";
import draggable from "vuedraggable";
import adminUnitHandling from '@/mixins/admin-unit-handling';
import foldersService from '@/services/FoldersService';
import {getFolderStatus} from "../../../../mixins/folder-status";

import FolderStatusCards from '@/components/folder/FolderStatusCards';

// Lazy loading de componentes
const FolderCard = () => ({
  component: import('@/components/folder/FolderCard'),
  loading: {
    template: '<div class="folder-card-skeleton"></div>'
  },
  delay: 200,
  timeout: 3000
});

const FolderGrid = () => ({
  component: import('@/components/folder/FolderGrid'),
  loading: {
    template: '<div class="folder-grid-skeleton"></div>'
  },
  delay: 200,
  timeout: 3000
});

export default {
  name: "inbox",
  components: {
    CurrentOwner,
    TermComponent,
    draggable,
    FolderCard,
    FolderGrid,
    FolderStatusCards
  },
  mixins: [adminUnitHandling],
  mounted() {
    // Inicializa corretamente os tipos de pastas
    // this.setInboxFoldersTypes();

    // Registra os eventos de bus apenas uma vez
    bus.$on("folderCreated", this.handleFolderCreated);
    bus.$on("loadInboxFolder", this.handleLoadInboxFolder);

    // Carrega avisos
    this.getNotices();

    // Adiciona listener para o evento de mudança de unidade administrativa
    window.addEventListener('admin-unit-changed', this.handleAdminUnitChange);
  },

  // Remove todos os event listeners ao destruir o componente
  beforeDestroy() {
    // Remove os event listeners do bus
    bus.$off("folderCreated", this.handleFolderCreated);
    bus.$off("loadInboxFolder", this.handleLoadInboxFolder);

    // Remove o event listener de mudança de unidade administrativa
    window.removeEventListener('admin-unit-changed', this.handleAdminUnitChange);
  },

  // O beforeDestroy foi movido próximo ao mounted acima
  data() {
    return {
      firstRender: true,
      orderFilters: [
        // {name: "Menor prazo", property: "limit_date", order: "asc"},
        // {name: "Maior prazo", property: "limit_date", order: "desc"},
        {
          name: "Tramitação mais antiga",
          property: "effective_updated_at",
          order: "asc"
        },
        {
          name: "Tramitação mais recente",
          property: "effective_updated_at",
          order: "desc"
        },
        // {
        //   name: "Por Setor",
        //   property: "administrative_unit",
        //   order: "asc"
        // }
      ],
      orderFilter: {
        name: "Tramitação mais recente",
        property: "effective_updated_at",
        order: "desc"
      },
      truncateText: truncate,
      shortName,
      isEmpty,
      pageNumber: 1,
      rowsPerPage: 20,
      showDialogNotice: false,
      notices: [],
      currentNotice: {},
      currentNoticeIndex: 0,
      draggingItemId: null,
      timer: null,
      dragDelay: 1500,
      viewMode: 'list',  // 'list' ou 'grid'
      isLoadingFolders: false  // Flag para controlar requisições simultâneas
    };
  },
  methods: {
    // Handler para eventos relacionados a pasta
    handleFolderCreated() {
      this.getFolders();
    },

    handleLoadInboxFolder() {
      this.getFolders();
    },
    startDrag(id) {
      console.log('startDrag called with id:', id);
      this.$store.commit("setDraggingFolder", id);
    },
    startTimer(id) {
      this.timer = setTimeout(() => {
        this.setDraggingItem(id);
      }, this.dragDelay);
    },
    stopTimer() {
      clearTimeout(this.timer);
      this.setDraggingItem(null);
    },
    setDraggingItem(id) {
      this.draggingItemId = id;

    },
    folderNotReceivedClass(folder) {
      if (folder.current_owner.id === this.user.id) {
        return "received-folder";
      }
      return "";
    },
    folderIcon(folder) {
      if (folder.current_owner.id === this.user.id) {
        return "/static/images/folder/folder-open-icon.svg";
      }
      return "/static/images/folder/folder-closed-icon.svg";
    },

    acronym(string) {
      return acronym(string);
    },
    currentUserIsOwner(currentOwner) {
      return this.user.id === currentOwner.id;
    },
    getFolders(loading = true) {
      // Evita requisições simultâneas
      if (this.isLoadingFolders) {
        console.info('Requisição já em andamento. Ignorando nova requisição de getFolders.')
        return;
      }

      this.isLoadingFolders = true;

      if (loading) {
        this.$store.dispatch("toggleLoader", {show: true});
      }

      // Verifica se a unidade administrativa foi definida
      if (!this.selectedAdministrativeUnit) {
        console.warn('Unidade administrativa não definida, usando a principal');
        const AdminUnitSwitchService = require('@/services/AdminUnitSwitchService').default;
        AdminUnitSwitchService.ensureSelectedUnit();
      }

      this.$store
        .dispatch("getFoldersInbox", {
          page: this.pageNumber,
          limit: this.rowsPerPage,
          order: this.orderFilter.order,
          sort: this.orderFilter.property,
          searchText: this.searchText || "",
          folderTypeId: this.folderTypeSelected.id || "",
          administrativeUnitId: this.selectedAdministrativeUnit || ""
        })
        .catch(error => {
          console.error("Erro ao carregar pastas:", error);
          this.$store.commit('toggleSnackbar', {
            show: true,
            timeout: 6000,
            color: 'error',
            message: 'Erro ao carregar pastas. Tente novamente.',
            messageIcon: 'mdi-alert-circle-outline',
          });
        })
        .finally(() => {
          this.$store.dispatch("toggleLoader", {show: false});
          this.$store.dispatch("setLoadingSearchText", false);
          this.isLoadingFolders = false;
        });
    },
    openFolder(id) {
      const route = this.$router.resolve(`/pastas/${id}/show`);
      window.open(route.href, "_blank");
    },
    formatDate(date) {
      return format(date, "DD/MM/YYYY");
    },
    formatMonthYear(date) {
      return format(date, "MMMM/YYYY", {locale: ptLocale});
    },
    resetFolders() {
      this.pageNumber = 1;
      this.$store.dispatch("setFolderTypeSelected", "");
      // this.$store.dispatch('getFoldersInbox');
      this.$store.dispatch("setActiveFolderFilterType", {});
    },
    setInboxFoldersTypes() {
      // foldersService.getFolderTypesOnInbox().then(response => {
      //   this.$store.dispatch("setFolderTypes", response.data);
      // });

    },
    orderDateToString(folder) {
      console.log('[DEBUG] orderDateToString called with folder:',this.orderFilter);
      switch (this.orderFilter.property) {
        case "limit_date":
          return folder.limit_date == null
            ? "Sem limite"
            : this.formatMonthYear(folder.limit_date);
        case "updated_at":
          // Usar o updated_at que vem do backend
          return this.formatMonthYear(folder.updated_at);
        case "created_at":
          if (!folder.last_transaction) {
            return "Processos não tramitados";
          }
          return this.formatMonthYear(folder.last_transaction.created_at);
        case "administrative_unit":
          if (folder.current_owner && folder.current_owner.administrative_unit) {
            return folder.current_owner.administrative_unit.abbreviation || folder.current_owner.administrative_unit.name;
          }
          return "Sem setor";
        default:
          return "Sem agrupamento";
      }
    },
    async getNotices() {
      const response = await NoticeService.getUserNotices().then(response => {
        this.notices = response.data;
        if (this.notices.length > 0) {
          this.currentNotice = this.notices[this.currentNoticeIndex];
          this.showDialogNotice = true;
        }
      });
    },
    close() {
      NoticeService.markNoticeAsViewed(this.currentNotice.id);
      if (this.currentNoticeIndex < this.notices.length - 1) {
        this.showDialogNotice = false;
        this.currentNotice = this.notices[++this.currentNoticeIndex];
        this.showDialogNotice = true;
      } else {
        this.showDialogNotice = false;
      }
    },
    // Manipulador para o evento de mudança de unidade administrativa
    handleAdminUnitChange(event = null) {
      // Reset de página e recarregamento dos dados
      this.pageNumber = 1;
      this.$nextTick(() => {
        this.getFolders(true);

        // Força atualização da contagem de pastas com pequeno atraso
        // para garantir que a contagem seja atualizada após os dados
        setTimeout(() => {
          this.$store.dispatch('getFoldersCount', {forceUpdate: true});
        }, 500);
      });
    }
  },
  computed: {
    user() {
      return this.$store.getters.user;
    },
    selectedAdministrativeUnit() {
      return this.$store.getters.selectedAdministrativeUnit;
    },
    loading() {
      return this.$store.getters.loading;
    },
    activeFolderFilterType() {
      return this.$store.getters.activeFolderFilterType;
    },
    folders() {
      return this.$store.getters.folders;
    },
    received() {
      if (this.$store.getters.folders.length === undefined) {
        return [];
      }

      let received = this.$store.getters.folders.filter(
        item => item.folder_section === "inbox"
      );

      // Adicionar received_status às pastas se necessário
      received = received.map(item => {
        if (item.last_transaction) {
          return {
            ...item,
            received_status: getFolderStatus(item),
          };
        }

        return item;
      });

      // if (!isEmpty(this.activeFolderFilterType)) {
      //   received = this.$store.getters.folders.filter(
      //     item => item.folder_section === 'inbox',
      //   );
      //   received = received.filter(
      //     item => item.folder_type.id === this.activeFolderFilterType.id,
      //   );
      //   this.$store.dispatch('setFoldersFound', received);
      // }
      return received;
    },
    groupedFolders() {
      const group = {};
      const orderedKeys = [];

      this.received.forEach(folder => {
        try {
          const date = folder.last_transaction
            ? new Date(folder.last_transaction.updated_at)
            : (folder.updated_at ? new Date(folder.updated_at) : null);

          const key = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}` : 'Sem data';

          if (!group[key]) {
            group[key] = { folders: [folder], date: date };
            orderedKeys.push(key);
          } else {
            group[key].folders.push(folder);
          }
        } catch (error) {
          console.error('[DEBUG] Erro ao processar pasta:', error, folder);
        }
      });


      orderedKeys.sort((a, b) => {
        try {
          if (group[a].date && group[b].date) {
            const comparison = this.orderFilter.order === 'asc'
              ? group[a].date - group[b].date
              : group[b].date - group[a].date;

            return comparison;
          }
          return 0;
        } catch (error) {
          console.error('[DEBUG] Erro ao comparar datas:', error, { a, b });
          return 0;
        }
      });

      const orderedGroups = {};
      orderedKeys.forEach(key => {
        try {
          if (!group[key]) {
            console.warn('[DEBUG] Grupo não encontrado:', key);
          }
          if (!group[key].date) {
            console.error('[DEBUG] Grupo sem campo date:', key, group[key]);
          }
          const label = group[key].date
            ? this.formatMonthYear(group[key].date)
            : 'Sem data';
          orderedGroups[label] = group[key].folders || [];
        } catch (error) {
          console.error('[DEBUG] Erro ao processar grupo:', key, error);
        }
      });

      return orderedGroups;
    },
    pages() {
      return this.$store.getters.foldersPagesCount;
    },
    foldersCount() {
      return this.$store.getters.activeTotalFolders;
    },
    pageStart() {
      return this.pageNumber * this.rowsPerPage - (this.rowsPerPage - 1);
    },
    pageStop() {
      return Math.min(
        this.pageStart + (this.rowsPerPage - 1),
        this.foldersCount
      );
    },
    searchText() {
      return this.$store.getters.searchText;
    },
    folderTypeSelected() {
      return this.$store.getters.folderTypeSelected;
    },
    inboxCounts() {
      return this.$store.getters.inboxCounts;
    }
  },
  watch: {
    pageNumber: {
      handler() {
        this.getFolders();
        this.$nextTick();
      },
      immediate: true
    },
    searchText(val) {
      if (val === null || val === "" || val.length > 2) {
        this.getFolders(false);
        this.$nextTick();
      }
    },
    folderTypeSelected() {
      this.getFolders();
      this.$nextTick();
    },
    orderFilter: {
      handler() {
        foldersService.clearCache();
        this.getFolders();
        this.$nextTick();
      }
    },
    selectedAdministrativeUnit: {
      handler() {
        this.pageNumber = 1;
        this.$nextTick();
      }
    },
    folders: {
      handler(newFolders) {
        this.$nextTick(() => {
          const cards = document.querySelectorAll('.folder-card');
          cards.forEach(card => {
            card.style.animation = 'none';
            card.offsetHeight; // Força reflow
            card.style.animation = null;
          });
        });
      },
      deep: true
    }
  }
};
</script>

<style scoped lang="scss">
@import '@/assets/styles/folder-list-margins.scss';

.received-folder {
  background: #f2f2f2;
  font-weight: 600 !important;

  &.caption {
    font-weight: 600 !important;
  }
}

// Override específico para inbox com position absolute
.list-folders-actions {
  position: absolute;
  z-index: 4;
  width: 97%;
}

.newLine {
  white-space: pre-wrap;
}

.dragging {
  background-color: #005ece;
}

// Skeleton loading styles
.folder-card-skeleton {
  height: 72px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 8px;
}

.folder-grid-skeleton {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  padding: 16px;

  .folder-card-skeleton {
    height: 200px;
  }
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.folder-list {
  .group {
    position: relative;
    overflow: hidden;
  }
}

.fade-cascade-enter-active {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 1;
  transform: translateY(0);
}
.fade-cascade-enter {
  opacity: 0;
  transform: translateY(20px);
}
.fade-cascade-enter-to {
  opacity: 1;
  transform: translateY(0);
}
.cascade-group > * {
  transition-delay: var(--cascade-delay, 0s);
  opacity: 1;
}
</style>



// WEBPACK FOOTER //
// src/views/folder/list/inbox/Inbox.vue